/**
 * Music + ambient bed module for Card Match. Mirrors Anagram audio/music.ts.
 *
 * Beds:
 *   lobby   — looping chill synthwave underscore in lobby
 *   round   — looping driving underscore during active rounds
 *   victory — short fanfare jingle, played one-shot at round-win
 */

import lobbyBedUrl from '../assets/music/lobby-bed.mp3?url';
import roundBedUrl from '../assets/music/round-bed.mp3?url';
import victoryUrl from '../assets/music/victory.mp3?url';

import { audioBus } from './bus.js';

export type MusicName = 'lobby' | 'round' | 'victory';

interface MusicSpec {
  url: string;
  loop: boolean;
  /** Per-track gain trim, multiplied with the master music gain. */
  gain: number;
}

const SPEC_LOBBY: MusicSpec = { url: lobbyBedUrl, loop: true, gain: 1.0 };
const SPEC_ROUND: MusicSpec = { url: roundBedUrl, loop: true, gain: 1.0 };
const SPEC_VICTORY: MusicSpec = { url: victoryUrl, loop: false, gain: 1.2 };

function specOf(name: MusicName): MusicSpec {
  if (name === 'lobby') return SPEC_LOBBY;
  if (name === 'round') return SPEC_ROUND;
  return SPEC_VICTORY;
}

interface Track {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
  gain: GainNode | null;
}

const tracks = new Map<MusicName, Track>();
let current: { name: MusicName; track: Track } | null = null;

function getTrack(name: MusicName): Track {
  let t = tracks.get(name);
  if (t) return t;
  const spec = specOf(name);
  const el = new Audio(spec.url);
  el.preload = 'auto';
  el.loop = spec.loop;
  let source: MediaElementAudioSourceNode | null = null;
  let gain: GainNode | null = null;
  const ctx = audioBus.context();
  if (ctx) {
    try {
      source = ctx.createMediaElementSource(el);
      gain = ctx.createGain();
      gain.gain.value = spec.gain;
      source.connect(gain);
      gain.connect(audioBus.musicGain());
    } catch (err) {
      console.warn(`[cm-music] media-element-source failed for ${name}:`, err);
    }
  }
  t = { el, source, gain };
  tracks.set(name, t);
  return t;
}

export const music = {
  play(name: MusicName): void {
    if (current && current.name === name && !current.track.el.paused) return;
    if (current) {
      try {
        current.track.el.pause();
      } catch {
        /* ignore */
      }
    }
    const track = getTrack(name);
    current = { name, track };
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    try {
      track.el.currentTime = 0;
      const p = track.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.debug(`[cm-music] autoplay blocked for ${name}:`, err);
        });
      }
    } catch (err) {
      console.warn(`[cm-music] play ${name} failed:`, err);
    }
  },
  stop(): void {
    if (!current) return;
    try {
      current.track.el.pause();
    } catch {
      /* ignore */
    }
    current = null;
  },
  resumeIfPending(): void {
    if (!current) return;
    const el = current.track.el;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* still blocked, ignore */
        });
      }
    }
  },
};
